import { Box } from "@mui/material";
import { type FC, useEffect, useMemo } from "react";
import { GoalDetailOwnFunds } from "web_myplann/Goals/Components/GoalDetailOwnFunds";
import { GoalDetailSolution } from "web_myplann/Goals/Components/GoalDetailSolution";
import { NewContractFlow } from "web_myplann/Goals/Components/PinContract/NewContractFlow";
import { PinContractChoiceDrawer } from "web_myplann/Goals/Components/PinContract/PinContractChoiceDrawer";
import { PinExistingContractDrawer } from "web_myplann/Goals/Components/PinContract/PinExistingContractDrawer";
import { getGoalPortfoliosByType } from "web_myplann/Goals/Helpers/getGoalPortfoliosByType";
import { withLockGuard } from "web_myplann/Goals/Helpers/withLockGuard";
import { useGoalPinStep } from "web_myplann/Goals/Hooks/useGoalPinStep";
import { useGoalPortfolioActions } from "web_myplann/Goals/Hooks/useGoalPortfolioActions";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	type GoalDetailResponseDTO,
	type GoalPortfolioListItemResponseDTO,
	GoalPortfolioTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";

export type WelcomePinTrigger = { hasOwnFunds: boolean };

type Props = {
	goal: GoalDetail;
	goalDto: GoalDetailResponseDTO | undefined;
	welcomePinTrigger: WelcomePinTrigger | null;
	onWelcomePinTriggerHandled: () => void;
	onLockedAction: (() => void) | null;
};

export const GoalPinningSection: FC<Props> = ({
	goal,
	goalDto,
	welcomePinTrigger,
	onWelcomePinTriggerHandled,
	onLockedAction,
}) => {
	const {
		pinSteps,
		pinPortfolioType,
		closePin,
		closePinStep,
		goToStep,
		openPin,
		openPinStep,
		openOwnFundsPin,
	} = useGoalPinStep(onLockedAction);

	const { pin, unpin, editAllocation, pinningId, unpinningId } =
		useGoalPortfolioActions({
			goalId: Number(goal.id),
			goalDto,
			onPinSettled: closePin,
		});

	useEffect(() => {
		if (!welcomePinTrigger) return;
		if (!onLockedAction) {
			const { hasOwnFunds } = welcomePinTrigger;
			openPin(
				hasOwnFunds
					? GoalPortfolioTypeEnum.SetAside
					: GoalPortfolioTypeEnum.Allocated,
				hasOwnFunds ? "choose" : "existing",
			);
		}
		onWelcomePinTriggerHandled();
	}, [welcomePinTrigger, onWelcomePinTriggerHandled, onLockedAction, openPin]);

	const handleUnpin = withLockGuard(onLockedAction, unpin);

	const handlePinSelect = (picked: GoalPortfolioListItemResponseDTO) => {
		pin(picked, pinPortfolioType);
	};

	const handlePinCreated = (portfolioId: number, onSettled: () => void) => {
		pin({ id: portfolioId, usagePercent: 0 }, GoalPortfolioTypeEnum.SetAside, {
			onSettled,
		});
	};

	const isSetAside = pinPortfolioType === GoalPortfolioTypeEnum.SetAside;

	const { allocated, setAside } = useMemo(
		() => getGoalPortfoliosByType(goal.portfolios),
		[goal.portfolios],
	);

	const hasOwnFunds = goal.ownFunds > 0;
	const readOnly = goal.completed;

	return (
		<>
			{hasOwnFunds && (
				<Box mt={1}>
					<GoalDetailOwnFunds
						goal={goal}
						portfolios={setAside}
						readOnly={readOnly}
						onAddPin={openOwnFundsPin}
						onUnpin={handleUnpin}
						unpinningId={unpinningId}
						onLockedAction={onLockedAction}
					/>
				</Box>
			)}
			<Box mt={1}>
				<GoalDetailSolution
					goal={goal}
					allocatedPortfolios={allocated}
					pinSteps={pinSteps}
					readOnly={readOnly}
					onPinStepOpen={openPinStep}
					onPinStepClose={closePinStep}
					onUnpin={handleUnpin}
					onEditAllocation={editAllocation}
					unpinningId={unpinningId}
					onLockedAction={onLockedAction}
				/>
			</Box>
			<PinContractChoiceDrawer
				open={!readOnly && pinSteps.includes("choose")}
				portfolioType={pinPortfolioType}
				onClose={() => closePinStep("choose")}
				onPickExisting={() => goToStep("existing")}
				onPickNew={() => goToStep("new")}
				onPickFuture={() => goToStep("future")}
			/>
			<PinExistingContractDrawer
				open={!readOnly && pinSteps.includes("existing")}
				onClose={() => closePinStep("existing")}
				onSelect={handlePinSelect}
				onAddNew={() => goToStep(isSetAside ? "new" : "future")}
				goalPortfolios={goal.portfolios}
				pinningId={pinningId}
			/>
			<NewContractFlow
				open={!readOnly && pinSteps.includes("new")}
				goal={goal}
				onClose={() => closePinStep("new")}
				onExit={closePin}
				onPinCreated={handlePinCreated}
			/>
		</>
	);
};
